import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Layout, Switch, Radio } from 'antd';
import { ProjectExtensionId, ProjectId } from '../../../../../../models/Types';
import {
  CreateProjectExtension,
  GetProjectExtensionSuggestion,
  ProjectExtension,
  ProjectExtensionSuggestion,
  UpdateProjectExtension,
} from '../../../../../../models/ProjectExtension';
import { Button, Input, Select } from '@prio365/prio365-react-library';
import { colon } from '../../../../../../util/forms';
import { OptionType } from '@prio365/prio365-react-library/lib/Select/components/Option';
import { Content } from 'antd/lib/layout/layout';
import { apiGetProjectExtensionSuggestion } from '../../../../api';
import { InformationBox } from '@prio365/prio365-react-library';
import Flex from '../../../../../../components/Flex';
import { makePrioStyles } from '../../../../../../theme/utils';
import {
  getProject,
  RootReducerState,
} from '../../../../../../apps/main/rootReducer';
import { useSelector } from 'react-redux';
import { Project } from '../../../../../../models/Project';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  submitButton: {
    position: 'absolute',
    bottom: '32px',
  },
  rootLayout: {
    margin: 0,
    height: '100%',
    backgroundColor: theme.colors.application.background.default,
  },
  editContent: {
    backgroundColor: theme.colors.application.background.default,
  },
}));

export interface PromanProjectExtensionFormModel {
  displayName: string;
  promanProjectId?: string;
  projectShortname?: string;
  hasDefaultAccess: boolean;
  createType: 'new' | 'link';
}

interface PromanProjectExtensionFormProps {
  className?: string;
  projectExtension: ProjectExtension;
  addProjectExtension: CreateProjectExtension;
  projectId: ProjectId;
  updateProjectExtension: (
    projectId: ProjectId,
    projectExtensionId: ProjectExtensionId,
    projectExtension: UpdateProjectExtension
  ) => Promise<ProjectExtension>;
  createProjectExtension: (
    projectId: ProjectId,
    createProjectExtension: CreateProjectExtension
  ) => Promise<ProjectExtension>;
}

export const PromanProjectExtensionForm: React.FC<
  PromanProjectExtensionFormProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    addProjectExtension,
    projectExtension,
    projectId,
    updateProjectExtension,
    createProjectExtension,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [disabled, setDisabled] = useState<boolean>(false);
  const [projects, setProjects] = useState<OptionType[]>([]);
  const sourceProject = useSelector<RootReducerState, Project>((state) =>
    getProject(state, projectId)
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [createType, setCreateType] = useState<'new' | 'link'>('new');
  const [selectedPromanProjectId, setSelectedPromanProjectId] =
    useState<string>(null);
  const [projectExtensionSuggestion, setProjectExtensionSuggestion] =
    useState<ProjectExtensionSuggestion>();
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const saveOrUpdateExtension = async (
    promanProjectExtensionFormModel: PromanProjectExtensionFormModel
  ) => {
    setLoading(true);
    setDisabled(true);

    if (projectExtension && !addProjectExtension) {
      // Update existing project extension
      let update: UpdateProjectExtension = {
        displayName: promanProjectExtensionFormModel.displayName,
        value:
          createType === 'new'
            ? promanProjectExtensionFormModel.projectShortname
            : null,
        key:
          createType === 'link'
            ? promanProjectExtensionFormModel.promanProjectId
            : null,
      };
      await updateProjectExtension(
        projectId,
        projectExtension.projectExtensionId,
        update
      );
    } else if (addProjectExtension && !projectExtension) {
      // Create new project extension
      let newProjectExtension: CreateProjectExtension = {
        displayName: promanProjectExtensionFormModel.displayName,
        value:
          createType === 'new'
            ? promanProjectExtensionFormModel.projectShortname
            : null,
        hasDefaultAccess: promanProjectExtensionFormModel.hasDefaultAccess,
        projectExtensionType: 'proman',
        key:
          createType === 'link'
            ? promanProjectExtensionFormModel.promanProjectId
            : null,
      };
      await createProjectExtension(projectId, newProjectExtension);
    }
    setDisabled(false);
    setLoading(false);
  };

  const handlePromanProjectSelection = (value: string) => {
    setSelectedPromanProjectId(value);
    // Find the selected project and set its display name
    const selectedProject = projects.find((project) => project.value === value);
    if (selectedProject) {
      form.setFieldsValue({
        displayName: selectedProject.label,
      });
    }
  };

  const handleCreateTypeChange = (e: any) => {
    const newCreateType = e.target.value;
    setCreateType(newCreateType);

    // Reset form fields when switching type
    if (newCreateType === 'new') {
      form.setFieldsValue({
        displayName: sourceProject?.name || 'PROMAN',
        promanProjectId: undefined,
        projectShortname: sourceProject?.number || '',
        isUnique: true,
      });
      setSelectedPromanProjectId(null);
    } else {
      form.setFieldsValue({
        displayName: '',
        projectShortname: undefined,
        promanProjectId: '',
      });
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const getPromanSuggestions = async () => {
      let getProjectExtensionSuggestion: GetProjectExtensionSuggestion = {
        key: null,
        value: null,
        projectExtensionType: 'proman',
      };
      const { data } = await apiGetProjectExtensionSuggestion(
        projectId,
        getProjectExtensionSuggestion
      );
      if (data) {
        setProjectExtensionSuggestion(data);
        setProjects(
          data.availableProjectExtensions?.map((promanProject) => {
            return {
              value: promanProject.key,
              label: promanProject.displayName,
            };
          })
        );
      }
    };

    getPromanSuggestions();
  }, [projectId]);

  // Set initial create type based on whether we're editing existing extension
  useEffect(() => {
    if (projectExtension) {
      // If editing existing extension, determine type based on whether it has a key or value
      setCreateType(projectExtension.key ? 'link' : 'new');
    }
  }, [projectExtension]);
  //#endregion

  return (
    <Layout className={classes.rootLayout}>
      <Form
        form={form}
        layout="vertical"
        className={classes.root}
        initialValues={{
          displayName:
            projectExtension?.displayName ?? sourceProject?.name ?? 'PROMAN',
          hasDefaultAccess: projectExtension?.hasDefaultAccess ?? true,
          promanProjectId: projectExtension?.key ?? '',
          projectShortname:
            projectExtension?.value ?? sourceProject?.number ?? '',
          createType: projectExtension
            ? projectExtension.key
              ? 'link'
              : 'new'
            : 'new',
        }}
        onFinish={(values) => {
          if (saveOrUpdateExtension) {
            saveOrUpdateExtension(values);
          }
        }}
        onValuesChange={(_, values) => {
          //nothingToDo
        }}
      >
        <Content className={classes.editContent}>
          {!projectExtension && (
            <>
              <Form.Item
                name="createType"
                colon={colon}
                label={t('projects:addIn.extensions.proman.form.createType')}
                required={true}
              >
                <Radio.Group
                  onChange={handleCreateTypeChange}
                  value={createType}
                >
                  <Radio value="new">
                    {t('projects:addIn.extensions.proman.form.newProject')}
                  </Radio>
                  <Radio value="link">
                    {t('projects:addIn.extensions.proman.form.linkExisting')}
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <InformationBox
                description={t(
                  'projects:addIn.extensions.proman.form.createTypeDescription'
                )}
              />
            </>
          )}
          {projectExtension && (
            <>
              <p>
                {t('projects:addIn.extensions.openExperience.notsupportedyet')}
              </p>
            </>
          )}
          {!projectExtension && (
            <>
              <Form.Item
                name="displayName"
                colon={colon}
                label={
                  createType === 'new'
                    ? t('projects:addIn.extensions.proman.form.longProjectName')
                    : t('projects:addIn.extensions.proman.form.longProjectName')
                }
                required={true}
                rules={[
                  {
                    required: true,
                    message: t('projects:form.validation.displayNameRequired'),
                  },
                ]}
              >
                <Input
                  full
                  disabled={
                    disabled || (createType === 'link' && !projectExtension)
                  }
                />
              </Form.Item>
              <InformationBox
                description={
                  createType === 'new'
                    ? t(
                        'projects:addIn.extensions.proman.form.longProjectNameDescription'
                      )
                    : t(
                        'projects:addIn.extensions.proman.form.longProjectNameDescription'
                      )
                }
              />
            </>
          )}
          {createType === 'new' && (
            <>
              <Form.Item
                name="projectShortname"
                colon={colon}
                label={t(
                  'projects:addIn.extensions.proman.form.projectShortname'
                )}
                required={true}
                rules={[
                  {
                    required: true,
                    message: t(
                      'projects:form.validation.form.projectShortnameRequired'
                    ),
                  },
                ]}
              >
                <Input full disabled={disabled} />
              </Form.Item>
              <InformationBox
                description={t(
                  'projects:addIn.extensions.proman.form.projectShortnameDescription'
                )}
              />
            </>
          )}

          {createType === 'link' && !projectExtension && (
            <>
              <Form.Item
                name="promanProjectId"
                colon={colon}
                label={t('projects:addIn.extensions.proman.form.project')}
                required={true}
                rules={[
                  {
                    required: true,
                    message: t('projects:form.validation.projectRequired'),
                  },
                ]}
              >
                <Select
                  options={projects}
                  disabled={projectExtension != null}
                  value={selectedPromanProjectId}
                  loading={loading}
                  onChange={handlePromanProjectSelection}
                />
              </Form.Item>
              <InformationBox
                description={t(
                  'projects:addIn.extensions.proman.promanProjectExtensionDescription'
                )}
              />
            </>
          )}

          {projectExtension && (
            <>
              {projectExtensionSuggestion?.availableProjectExtensions?.map(
                (promanProject) => {
                  if (promanProject.key.toString() === projectExtension?.key) {
                    return (
                      <p key={promanProject.key}>
                        {t('projects:addIn.extensions.proman.form.project')}:
                        {promanProject.displayName}
                      </p>
                    );
                  } else {
                    return '';
                  }
                }
              )}
            </>
          )}

          {!projectExtension && (
            <>
              <Form.Item
                name="isUnique"
                colon={colon}
                required={true}
                initialValue={true}
                label={t('projects:addIn.extensions.proman.form.isUnique')}
                rules={[
                  {
                    required: true,
                    message: t('projects:form.validation.isUnique'),
                  },
                ]}
                valuePropName="checked"
              >
                <Switch disabled={loading || disabled} />
              </Form.Item>
              <InformationBox
                description={t(
                  'projects:addIn.extensions.proman.isUniqueDescription'
                )}
              />

              <Form.Item
                name="hasDefaultAccess"
                colon={colon}
                required={true}
                label={t('projects:addIn.extensions.form.hasDefaultAccess')}
                rules={[
                  {
                    required: true,
                    message: t(
                      'projects:form.validation.hasDefaultAccessRequired'
                    ),
                  },
                ]}
                valuePropName="checked"
              >
                <Switch disabled={loading || disabled} />
              </Form.Item>
            </>
          )}
        </Content>
        <Flex.Row flex={1} justifyContent="flex-end">
          {!projectExtension && (
            <Button
              htmlType="submit"
              loading={disabled}
              disabled={disabled}
              className={classes.submitButton}
            >
              {t('projects:addIn.extensions.form.save')}
            </Button>
          )}
        </Flex.Row>
      </Form>
    </Layout>
  );
};

export default PromanProjectExtensionForm;
